import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CheckCircle2, Circle, Loader2, AlertTriangle, XCircle, MinusCircle, Download, RotateCcw, Play, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const STEP_LABEL = { cams: "CAMS", karvy: "KARVY", volt: "VOLT", zip: "Sub-broker ZIP" };
const STAGE_TEXT = {
  waiting_files: "Waiting for this month's files",
  step2_cams: "Building CAMS workbook",
  step2_karvy: "Building KARVY workbook",
  step2_volt: "Building VOLT workbook",
  step3: "Step 2 done — generating sub-broker ZIP",
  needs_attention: "Paused — needs your attention",
  done: "Step 2 complete",
  error: "Failed",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

const StatusIcon = ({ status }) => {
  if (status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-blue-600" />;
  if (status === "needs_attention") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (status === "error") return <XCircle className="h-4 w-4 text-red-600" />;
  if (status === "skipped") return <MinusCircle className="h-4 w-4 text-slate-400" />;
  return <Circle className="h-4 w-4 text-slate-300" />;
};

const StepRow = ({ k, s }) => (
  <div className="flex items-center justify-between rounded-md border border-slate-100 bg-white px-3 py-2 text-sm" data-testid={`autopilot-step-${k}`}>
    <span className="flex items-center gap-2"><StatusIcon status={s?.status} />{STEP_LABEL[k]}</span>
    <span className="text-xs text-slate-500">
      {s?.status === "done" && (s.row_count ? `${Number(s.row_count).toLocaleString("en-IN")} rows · ` : "") + fmt(s.finished_at)}
      {s?.status === "running" && `running since ${fmt(s.started_at)}`}
      {s?.status === "error" && <span className="text-red-600">{s.error} (attempt {s.attempts})</span>}
      {s?.status === "needs_attention" && <span className="text-amber-700">waiting for tagging</span>}
      {s?.status === "skipped" && "skipped"}
      {(!s || s.status === "pending") && "pending"}
    </span>
  </div>
);

export const useAutopilot = (onStateChange) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/commissions/autopilot/status`, auth());
      setSt(r.data);
      onStateChange && onStateChange(r.data);
    } catch (_e) { /* silent */ }
  }, [onStateChange]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  const act = async (path, msg) => {
    setBusy(path);
    try {
      await axios.post(`${API}/commissions/autopilot/${path}`, {}, auth());
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBusy("");
    }
  };
  return { st: st && st.cycle_key ? st : null, busy, act };
};

const AttentionBox = ({ att, busy, act, onTagKarvy, onTagVolt }) => {
  const canTag = (att.step === "karvy" && att.error === "unmapped_subbroker") || (att.step === "volt" && att.error === "unmapped_ifa");
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="autopilot-attention">
      <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4" /> Needs your attention — {att.step?.toUpperCase()}</div>
      <p className="mt-1">{att.gap_count} item(s) need tagging ({att.error}). Auto-retries every 5 minutes and resumes once tagged.</p>
      {canTag && (
        <Button size="sm" className="mt-2 mr-2" data-testid="autopilot-tag-now-btn"
          onClick={() => (att.step === "karvy" ? onTagKarvy(att.gaps || []) : onTagVolt(att.gaps || []))}>
          Tag now
        </Button>
      )}
      <Button size="sm" variant="outline" className="mt-2" disabled={!!busy} data-testid="autopilot-retry-btn"
        onClick={() => act("retry", "Retry queued — resumes within a minute")}>
        <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Retry now
      </Button>
    </div>
  );
};

export const AutopilotStep2Panel = ({ ap, onTagKarvy, onTagVolt }) => {
  const { st, busy, act } = ap;
  if (!st) return null;
  const ready = Object.values(st.source_ready || {});
  const anyReady = ready.some(Boolean);
  const allReady = ready.length > 0 && ready.every(Boolean);
  const s2stage = ["step3", "done"].includes(st.stage) ? "done" : st.stage;
  return (
    <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="autopilot-step2-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="font-medium text-slate-800">Automatic run{st.period ? ` · ${st.period}` : ""}</span>
        <span className="text-xs text-blue-800" data-testid="autopilot-stage">{STAGE_TEXT[s2stage] || s2stage}</span>
      </div>
      {["cams", "karvy", "volt"].map((k) => <StepRow key={k} k={k} s={st.steps?.[k]} />)}
      {st.attention && st.attention.step !== "zip" && (
        <AttentionBox att={st.attention} busy={busy} act={act} onTagKarvy={onTagKarvy} onTagVolt={onTagVolt} />
      )}
      <div className="flex flex-wrap gap-2">
        {st.stage === "waiting_files" && anyReady && !allReady && !st.allow_partial && (
          <Button size="sm" variant="outline" disabled={!!busy} data-testid="autopilot-run-available-btn"
            onClick={() => act("run-available", "Running with the files that are available")}>
            <Play className="mr-1.5 h-3.5 w-3.5" /> Run with available files
          </Button>
        )}
        {st.stage === "error" && (
          <Button size="sm" variant="outline" disabled={!!busy} data-testid="autopilot-error-retry-btn"
            onClick={() => act("retry", "Retry queued")}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Retry
          </Button>
        )}
      </div>
    </div>
  );
};

const NotifyEmails = () => {
  const [emails, setEmails] = useState("");
  const [cc, setCc] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/commissions/autopilot/notify-emails`, auth())
      .then((r) => { setEmails((r.data?.emails || []).join(", ")); setCc((r.data?.cc || []).join(", ")); }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/commissions/autopilot/notify-emails`, { emails, cc }, auth());
      setEmails((r.data?.emails || []).join(", "));
      setCc((r.data?.cc || []).join(", "));
      setEditing(false);
      toast.success("Notification emails saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };
  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600" data-testid="autopilot-notify-emails">
        <Mail className="h-3.5 w-3.5" /> Download link is emailed to: <b data-testid="autopilot-notify-emails-value">{emails || "—"}</b>
        {cc && <>· CC: <b data-testid="autopilot-notify-cc-value">{cc}</b></>}
        <button type="button" className="text-blue-700 underline" onClick={() => setEditing(true)} data-testid="autopilot-notify-emails-edit">Change</button>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="admin@company.com, accounts@company.com"
        className="h-8 max-w-md text-xs" data-testid="autopilot-notify-emails-input" />
      <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="CC (optional)"
        className="h-8 max-w-xs text-xs" data-testid="autopilot-notify-cc-input" />
      <Button size="sm" onClick={save} disabled={saving} data-testid="autopilot-notify-emails-save">Save</Button>
      <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="autopilot-notify-emails-cancel">Cancel</Button>
    </div>
  );
};

export const AutopilotStep3Panel = ({ ap, onDownloadZip }) => {
  const { st, busy, act } = ap;
  if (!st) return null;
  const z = st.steps?.zip;
  const zp = st.zip_progress;
  return (
    <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="autopilot-step3-panel">
      <StepRow k="zip" s={z} />
      {z?.status === "running" && zp?.message && (
        <p className="text-xs text-blue-700" data-testid="autopilot-zip-progress">{zp.message}{typeof zp.percent === "number" ? ` · ${zp.percent}%` : ""}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {st.stage === "done" && st.zip_job_id && (
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="autopilot-download-zip-btn"
            onClick={() => onDownloadZip(st.zip_job_id)}>
            <Download className="mr-1.5 h-3.5 w-3.5" /> Download auto-generated ZIP
          </Button>
        )}
        {["done", "error"].includes(st.stage) && (
          <Button size="sm" variant="ghost" disabled={!!busy} data-testid="autopilot-rerun-btn"
            onClick={() => window.confirm("Re-run Step 2 and Step 3 for this month from scratch?") && act("rerun", "Automatic run restarted")}>
            Run again
          </Button>
        )}
        <NotifyEmails />
        {st.completed_at && <span className="text-xs text-slate-500" data-testid="autopilot-completed-at">Completed {fmt(st.completed_at)}</span>}
        {st.stage === "done" && st.email_sent_to?.length > 0 && (
          <span className="text-xs text-emerald-700" data-testid="autopilot-email-sent">Download link emailed to {st.email_sent_to.join(", ")}</span>
        )}
        {st.stage === "done" && st.email_error && (
          <span className="text-xs text-red-600" data-testid="autopilot-email-error">Email failed: {st.email_error}</span>
        )}
      </div>
    </div>
  );
};
